import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Spin, Alert, Button, Tabs, Layout, Input, Modal, message } from 'antd';
import { useAuth } from '../context/AuthContext';
import { usePatientChart } from '../context/PatientChartContext';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../hooks/usePermissions';

// Import History, Allergy, Medication, Vaccination components
import MedicalHistorySection from '../components/MedicalHistorySection';
import SurgicalHistorySection from '../components/SurgicalHistorySection';
import FamilyHistorySection from '../components/FamilyHistorySection';
import VaccinationStatusSection from '../components/VaccinationStatusSection';
import PatientAllergyList from '../components/PatientAllergyList';
import PatientMedications from '../components/PatientMedications';
import LifestyleHistorySection from '../components/LifestyleHistorySection';
import DemographicsSection from '../components/DemographicsSection';
import ClinicianReview from '../components/ClinicianReview';

// Import other sections used in tabs
import PatientEncounters from '../components/PatientEncounters';
import PatientOrders from '../components/PatientOrders';
// import PatientLifestyleAssessments from '../components/PatientLifestyleAssessments'; // REMOVED
import PatientProblemList from '../components/PatientProblemList';
import PatientCalendar from '../components/PatientCalendar';
import PatientSmartGoals from '../components/PatientSmartGoals';

// Import components needed for patient tabs

import PatientFlowsheet from '../components/PatientFlowsheet';
import PatientDocumentsAndHealthLibrary from '../components/PatientDocumentsAndHealthLibrary';
// Import new tabs
import UnifiedHealthWellnessDashboard from '../components/UnifiedHealthWellnessDashboard';
import HealthSnapshotV2 from '../components/HealthSnapshotV2'; // Import V2
import SystemMediatedSimulationSandbox from '../components/SystemMediatedSimulationSandbox'; // Import the system-mediated simulation
import ActionPlansTab from '../components/ActionPlansTab'; // Import Action Plans Tab



const { TabPane } = Tabs;
const { Content } = Layout;




const PatientDetailPage = ({ patientId: propPatientId }) => {
  const { patientId: urlPatientId } = useParams(); // Extract patientId from URL parameters
  // Use patientId from props (drawer) or URL parameters (direct navigation)
  const patientId = propPatientId || urlPatientId;
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { token } = useAuth();
  const { t } = useTranslation();
  const { canAccessFeature } = usePermissions();
  const { chartAction, clearChartAction } = usePatientChart();

  // Debug permission check
  const canAccessHealthMetrics = canAccessFeature('health-metrics');
  console.log('🔍 Health Metrics Permission Check:', {
    canAccessHealthMetrics,
    feature: 'health-metrics'
  });

  const [showMedicationAddModalForOrder, setShowMedicationAddModalForOrder] = useState(false);
  const [refreshOrdersTrigger, setRefreshOrdersTrigger] = useState(0);
  const [refreshMedicationsTrigger, setRefreshMedicationsTrigger] = useState(0);
  const [refreshTimelineTrigger, setRefreshTimelineTrigger] = useState(0); // Added for timeline
  const [refreshHealthSnapshotTrigger, setRefreshHealthSnapshotTrigger] = useState(0);
  const [clinicalFlags, setClinicalFlags] = useState(null);

  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState('medical-record');
  const [highlightOrderId, setHighlightOrderId] = useState(null);

  // SMART Goals state
  const [problems, setProblems] = useState([]);
  const [smartGoalModalVisible, setSmartGoalModalVisible] = useState(false);
  const [selectedProblemForGoal, setSelectedProblemForGoal] = useState(null);
  const [refreshSmartGoalsTrigger, setRefreshSmartGoalsTrigger] = useState(0);

  const handleOrderChange = React.useCallback(() => {
    setRefreshOrdersTrigger(prev => prev + 1);     // Refresh orders list
    setRefreshMedicationsTrigger(prev => prev + 1); // Keep existing medication refresh
    setRefreshTimelineTrigger(prev => prev + 1);   // Add timeline refresh
  }, []);

  // Fetch problems for SMART goals
  const fetchProblems = React.useCallback(async () => {
    if (!patientId) return;
    try {
      const response = await axios.get(`/patients/${patientId}/problems`);
      console.log('Problems API response:', response.data); // Debug logging

      // Handle different response structures
      if (response.data && response.data.data && response.data.data.problems) {
        setProblems(response.data.data.problems);
      } else if (response.data && response.data.problems) {
        // Fallback for different response structure
        setProblems(response.data.problems);
      } else {
        console.warn('Unexpected problems API response structure:', response.data);
        setProblems([]);
      }
    } catch (error) {
      console.error('Error fetching problems:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      setProblems([]); // Set empty array on error
    }
  }, [patientId]);

  // Handle SMART goal creation from problem list
  const handleCreateSmartGoal = React.useCallback((problem) => {
    setSelectedProblemForGoal(problem);
    setActiveTabKey('smart-goals'); // Switch to SMART goals tab
  }, []);

  // Handle SMART goal updates
  const handleSmartGoalUpdate = React.useCallback(() => {
    setRefreshSmartGoalsTrigger(prev => prev + 1);
    setRefreshTimelineTrigger(prev => prev + 1); // Refresh timeline when goals change
    // Clear selected problem after goal update to prevent modal persistence
    setSelectedProblemForGoal(null);
  }, []);

  // Handle modal cancel to clear selected problem
  const handleSmartGoalModalCancel = React.useCallback(() => {
    setSelectedProblemForGoal(null);
  }, []);


  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    const highlight = params.get('highlight');
    if (tab) setActiveTabKey(tab);
    if (highlight) setHighlightOrderId(highlight);
  }, [location.search, navigate]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    const loadData = async () => {
      if (!patientId) {
        if (isMounted) {
          setError("No patient selected.");
          setLoading(false);
        }
        return;
      }

      try {
        const [patientRes, flagsRes] = await Promise.all([
          axios.get(`/patients/${patientId}?populate=primaryMD,primaryNurse`),
          axios.get(`/patients/${patientId}/clinical-flags`)
        ]);
        if (isMounted) {
          setPatient(patientRes.data.data.patient);
          setClinicalFlags(flagsRes.data.data);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.response?.data?.message || 'Failed to load patient data.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [patientId]);

  // Fetch problems when patient changes
  useEffect(() => {
    fetchProblems();
  }, [fetchProblems]);

  // Handle chart actions (e.g., from Weekly IDT List)
  useEffect(() => {
    if (chartAction && chartAction.patientId === patientId) {
      const handleChartAction = async () => {
        try {
          if (chartAction.type === 'EDIT_IDT_NOTE' || chartAction.type === 'VIEW_IDT_NOTE') {
            // Switch to encounters tab first
            setActiveTabKey('encounters');

            // Small delay to ensure tab is switched and PatientEncounters is mounted
            setTimeout(() => {
              // The PatientEncounters component will handle the actual action
              // We keep the action in context so it can be picked up by PatientEncounters
            }, 100);
          }
        } catch (error) {
          console.error('Error handling chart action:', error);
          message.error('Failed to perform the requested action.');
          clearChartAction();
        }
      };

      handleChartAction();
    }
  }, [chartAction, patientId, clearChartAction]);

  if (loading) { return ( <div style={{ textAlign: 'center', marginTop: '50px' }}><Spin size="large" /></div> ); }
  if (error) { return <Alert message={`Error loading chart: ${error}`} type="error" showIcon />; }
  if (!patient) { return <Alert message="Patient data could not be loaded." type="warning" showIcon />; }

  const handleDeletePatient = async () => {
    if (!deletePassword) { message.error('Password is required to delete a patient.'); return; }
    setDeleteLoading(true);
    try {
      if (!token) { message.error('Authentication required to delete patient.'); setDeleteLoading(false); setIsPasswordModalVisible(false); return; }
      message.loading({ content: 'Deleting patient...', key: 'deletePatient' });
      const deleteConfig = {
        headers: { 'Authorization': `Bearer ${token}` },
        data: { password: deletePassword },
        bypassGlobalInterceptor: true // Prevent auto-logout on 401 (wrong password)
      };
      await axios.delete(`/patients/${patientId}`, deleteConfig);
      message.success({
        content: `Patient "${patient.firstName} ${patient.lastName}" deleted successfully!`,
        key: 'deletePatient',
        duration: 3,
        style: {
          marginTop: '20vh',
        },
      });
      setIsPasswordModalVisible(false); setDeletePassword(''); setDeleteLoading(false);

      // Small delay before redirect to ensure message is visible
      setTimeout(() => {
        window.location.href = '/patients';
      }, 1000);
    } catch (error) {
      setDeleteLoading(false);
      if (error.response?.status === 403) {
        message.error({
          content: 'You do not have permission to delete patients.',
          key: 'deletePatient',
          duration: 4,
          style: {
            marginTop: '20vh',
          },
        });
      }
      else if (error.response?.status === 401) {
        message.error({
          content: 'Incorrect password. Please try again.',
          key: 'deletePatient',
          duration: 4,
          style: {
            marginTop: '20vh',
          },
        });
      }
      else {
        const errorMsg = error.response?.data?.message || 'Failed to delete patient.';
        message.error({
          content: `Error: ${errorMsg}`,
          key: 'deletePatient',
          duration: 4,
          style: {
            marginTop: '20vh',
          },
        });
      }
    }
  };

  return (
    <Layout style={{ background: '#fff', position: 'relative', minHeight: '100vh' }}>
      <style>
        {`
          .ant-tabs-tab:has([style*="color: #1890ff"]) {
            background: linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%) !important;
            border-color: #1890ff !important;
            box-shadow: 0 2px 8px rgba(24, 144, 255, 0.2) !important;
          }
          .ant-tabs-tab:has([style*="color: #1890ff"]):hover {
            background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%) !important;
            transform: translateY(-1px);
            transition: all 0.3s ease;
          }
          .ant-tabs-tab:has([style*="color: #1890ff"]).ant-tabs-tab-active {
            background: linear-gradient(135deg, #1890ff 0%, #40a9ff 100%) !important;
            border-color: #1890ff !important;
          }
          .ant-tabs-tab:has([style*="color: #1890ff"]).ant-tabs-tab-active span {
            color: white !important;
          }
        `}
      </style>
      <Content style={{ flex: 1, overflowY: 'auto' }}>
        {/*
          ARCHITECTURAL NOTE:
          To ensure performance and prevent state leakage between tabs, only the active tab's component is rendered.
          ALL new tabs MUST follow this conditional rendering pattern:
          {activeTabKey === 'your-tab-key' && <YourComponent />}
        */}
        <Tabs activeKey={activeTabKey} onChange={setActiveTabKey} type="card" style={{ position: 'sticky', top: 0, zIndex: 1, background: '#fff' }}>
          <TabPane tab={t('patientChart.tabs.medicalRecord')} key="medical-record">
            {activeTabKey === 'medical-record' && (
              <Tabs defaultActiveKey="demographics" tabPosition="left" type="line">
                <TabPane tab="Demographics" key="demographics"> <DemographicsSection patientId={patientId} /> </TabPane>
                <TabPane tab="Medical History" key="medical"> <MedicalHistorySection patientId={patientId} /> </TabPane>
                <TabPane tab="Surgical History" key="surgical"> <SurgicalHistorySection patientId={patientId} /> </TabPane>
                <TabPane tab="Family History" key="family"> <FamilyHistorySection patientId={patientId} /> </TabPane>
                <TabPane tab="Lifestyle History" key="lifestyle"><LifestyleHistorySection patientId={patientId} /></TabPane>
                <TabPane tab="Allergies" key="allergies"> <PatientAllergyList patientId={patientId} /> </TabPane>
                <TabPane tab="Current Medications" key="medications"> <PatientMedications patientId={patientId} /></TabPane>
                <TabPane tab="Vaccination History" key="vaccinations"> <VaccinationStatusSection patientId={patientId} /> </TabPane>

                <TabPane tab="Clinician Review" key="clinician-review">
                  <ClinicianReview
                    patientId={patientId}
                    onFlagsSaved={async () => {
                      const flagsRes = await axios.get(`/patients/${patientId}/clinical-flags`);
                      setClinicalFlags(flagsRes.data.data);
                      setRefreshHealthSnapshotTrigger(prev => prev + 1);
                    }}
                  />
                </TabPane>
              </Tabs>
            )}
          </TabPane>

          <TabPane tab={t('patientChart.tabs.flowsheet')} key="flowsheet" style={{ minHeight: '500px', overflow: 'hidden', padding: 0 }}>
            {activeTabKey === 'flowsheet' && <PatientFlowsheet patientId={patientId} />}
          </TabPane>
          <TabPane tab={t('patientChart.tabs.encounters')} key="encounters">
            {activeTabKey === 'encounters' && <PatientEncounters patientId={patientId} />}
          </TabPane>
          {canAccessHealthMetrics && (
            <TabPane tab={t('patientChart.tabs.healthMetrics')} key="health-snapshot-v2">
              {activeTabKey === 'health-snapshot-v2' && <HealthSnapshotV2 patientId={patientId} clinicalFlags={clinicalFlags} />}
            </TabPane>
          )}
          <TabPane tab={t('patientChart.tabs.problemList')} key="active-medical-problems">
            {activeTabKey === 'active-medical-problems' && (
              <PatientProblemList
                patientId={patientId}
                onCreateSmartGoal={handleCreateSmartGoal}
              />
            )}
          </TabPane>
          <TabPane tab={t('patientChart.tabs.smartGoals')} key="smart-goals">
            {activeTabKey === 'smart-goals' && (
              <PatientSmartGoals
                patientId={patientId}
                problems={problems}
                refreshTrigger={refreshSmartGoalsTrigger}
                selectedProblem={selectedProblemForGoal}
                onGoalUpdate={handleSmartGoalUpdate}
                onModalCancel={handleSmartGoalModalCancel}
              />
            )}
          </TabPane>
          <TabPane tab="Action Plans" key="action-plans">
            {activeTabKey === 'action-plans' && <ActionPlansTab patientId={patientId} />}
          </TabPane>
          <TabPane tab={t('patientChart.tabs.orders')} key="orders">
            {activeTabKey === 'orders' && (
              <PatientOrders
                patientId={patientId}
                openMedicationModalForOrders={() => setShowMedicationAddModalForOrder(true)}
                refreshTrigger={refreshOrdersTrigger}
                onOrderUpdated={handleOrderChange}
                highlightOrderId={highlightOrderId}
              />
            )}
          </TabPane>
          <TabPane tab={t('patientChart.tabs.medications')} key="medications">
            {activeTabKey === 'medications' && (
              <PatientMedications
                patientId={patientId}
                triggerOpenAddModal={showMedicationAddModalForOrder}
                onAddModalClose={() => setShowMedicationAddModalForOrder(false)}
                onMedicationAndOrderAdded={() => {
                  setRefreshOrdersTrigger(prev => prev + 1);
                  setRefreshTimelineTrigger(prev => prev + 1);
                  setShowMedicationAddModalForOrder(false);
                }}
                onMedicationUpdated={() => {
                  setRefreshOrdersTrigger(prev => prev + 1);
                  setRefreshTimelineTrigger(prev => prev + 1);
                }}
                refreshTrigger={refreshMedicationsTrigger}
              />
            )}
          </TabPane>
          <TabPane tab={t('patientChart.tabs.documents')} key="documents">
            {activeTabKey === 'documents' && <PatientDocumentsAndHealthLibrary patientId={patientId} />}
          </TabPane>
          <TabPane
            tab={<span style={{ color: '#1890ff', fontWeight: 'bold' }}>{t('patientChart.tabs.healthOverview')}</span>}
            key="health-overview"
          >
            {activeTabKey === 'health-overview' && <UnifiedHealthWellnessDashboard patientId={patientId} clinicalFlags={clinicalFlags} refreshTrigger={refreshHealthSnapshotTrigger} />}
          </TabPane>
          <TabPane
            tab={<span style={{ color: '#1890ff', fontWeight: 'bold' }}>{t('patientChart.tabs.timeline')}</span>}
            key="calendar"
          >
            {activeTabKey === 'calendar' && (
              <PatientCalendar
                patientId={patientId}
                refreshTrigger={refreshTimelineTrigger}
                onOrderCreated={handleOrderChange}
              />
            )}
          </TabPane>

          <TabPane
            tab={<span style={{ color: '#28a745', fontWeight: 'bold' }}>🧪 Health Simulation</span>}
            key="simulation-sandbox"
          >
            {activeTabKey === 'simulation-sandbox' && <SystemMediatedSimulationSandbox patientId={patientId} />}
          </TabPane>
        </Tabs>
      </Content>

      <Modal title="Confirm Patient Deletion" open={isPasswordModalVisible} onCancel={() => setIsPasswordModalVisible(false)}
        footer={[ <Button key="cancel" onClick={() => setIsPasswordModalVisible(false)}>Cancel</Button>, <Button key="delete" type="primary" danger loading={deleteLoading} onClick={handleDeletePatient}>Delete Patient</Button>, ]} >
        <p>Are you sure you want to delete this patient? This action cannot be undone.</p>
        <p>Please enter your password to confirm:</p>
        <Input.Password placeholder="Enter your password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} onPressEnter={handleDeletePatient} style={{ marginTop: '10px' }} />
      </Modal>
    </Layout>
  );
};

export default PatientDetailPage;
